/**
 * Notification Context
 * Manages application-wide notifications
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from "react";
import type {
  Notification,
  NotificationContextState,
} from "@/types/notification";
import {
  notificationsService,
  type APINotification,
} from "@/services/notificationsService";

const NotificationContext = createContext<NotificationContextState | null>(
  null,
);

const mapNotificationType = (value?: string | null): Notification["type"] => {
  switch ((value ?? "").toLowerCase()) {
    case "service_requested":
    case "service_started":
    case "low_stock":
    case "service_completed":
    case "quote_requested":
    case "payment_received":
      return value as Notification["type"];
    default:
      return "service_requested";
  }
};

const mapNotificationPriority = (
  value?: string | null,
): Notification["priority"] => {
  switch ((value ?? "").toLowerCase()) {
    case "high":
    case "medium":
    case "low":
      return value as Notification["priority"];
    default:
      return "medium";
  }
};

const mapAPINotification = (n: APINotification): Notification => ({
  id: String(n.id),
  type: mapNotificationType(n.type),
  priority: mapNotificationPriority(n.priority),
  title: n.title ?? "Notificación",
  message: n.message ?? "",
  timestamp: new Date(n.created_at ?? n.timestamp ?? Date.now()),
  read: n.is_read ?? n.read ?? false,
  actionUrl: n.action_url ?? undefined,
  metadata: (n.metadata ?? undefined) as Notification["metadata"],
});

/**
 * Notification Provider Props
 */
interface NotificationProviderProps {
  children: ReactNode;
}

/**
 * Notification Provider Component
 */
export function NotificationProvider({
  children,
}: NotificationProviderProps): React.ReactElement {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const rows = await notificationsService.list({ page_size: 100 });
        setNotifications(rows.map(mapAPINotification));
      } catch (err) {
        console.error("Error cargando notificaciones:", err);
      }
    };
    void load();
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const addNotification = useCallback(
    (notification: Omit<Notification, "id" | "timestamp" | "read">) => {
      const newNotification: Notification = {
        ...notification,
        id: `notif-${Date.now()}-${Math.random()}`,
        timestamp: new Date(),
        read: false,
      };

      setNotifications((prev) => [newNotification, ...prev]);
    },
    [],
  );

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n)),
    );

    notificationsService.markAsRead(id).catch((err) => {
      console.error("Error marcando notificación como leída:", err);
    });
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));

    notificationsService.markAllAsRead().catch((err) => {
      console.error("Error marcando todas como leídas:", err);
    });
  }, []);

  const deleteNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));

    notificationsService.deleteNotification(id).catch((err) => {
      console.error("Error eliminando notificación:", err);
    });
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const value: NotificationContextState = {
    notifications,
    unreadCount,
    addNotification,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    clearAll,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * Hook to use notification context
 */
export function useNotifications(): NotificationContextState {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within NotificationProvider",
    );
  }
  return context;
}
